import {
    DraggableCardConfig,
    HeaderLotChip,
    InfoLotChip,
    LegResponseStructure,
    LotCardConfig,
    LotResponseStructure,
    PlanningDragData
} from '../../models/planningPageTypes';
import { LOT_FILTER_VALUES } from '../configuration/consts/lotFilters';
import {
    DISABLED_TRANSPORT_ORDER_STATUSES,
    PICKUP_PLANNING_CARD_CHIPS,
    STATUS_VALUE_TO_LABEL_MAP,
    TRAFFIC_MODE_LEG_TYPES,
    TRANSPORT_MODE_VALUE_TO_LABEL_MAP
} from '../configuration/consts/pickupPlanning';
import { MultipleSortParams, TransportOrderListRecord } from '@nagel-types';
import { ReorderableSortingType } from '../components/reorderable-sorting/sorting-const';
import { formatToLocalDate, getCalcFieldSortObject, getTimeOfDay, roundToTwoDecimalPlaces } from '@nagel-utils';
import { TRANSPORT_ORDER_KEYS } from '../configuration/consts/transportOrderKeys';
import { LazyLoadDirective } from '@nagel-directives';
import { QueryList } from '@angular/core';

const {
    WEIGHT, VOLUME_PALLET_SPACES, FLOOR_PALLET_SPACES, DELIVERY_DATE_FROM, DELIVERY_DATE_TO,
    PLANNED_TIME, PEOPLE, TRAFFIC_FLOW, AMBIENT, LIQUID, FROZEN, DRY, FRESH,
    ULTRA_FRESH, FRUITS_AND_VEGETABLES
} = PICKUP_PLANNING_CARD_CHIPS;

const MAX_WEIGHT = 24;
const MAX_VOLUME_PALLET_SPACES = 32;
const MAX_FLOOR_PALLET_SPACES = 32;
const FIXED_NUMBER_OF_DECIMALS = 2;


export const getMappedLot = (responseLot: LotResponseStructure, legs: LegResponseStructure[], index: number): LotCardConfig  => {
    // const initialLeg = legs[0] || {} as LegResponseStructure;

    const {
        originName,
        originCountry,
        originZipCode,
        originCity,
        originStreet,
    } = responseLot;

    return {
        id: responseLot.id,
        identificationNumber: responseLot.number?.toString() || index?.toString() || '0',
        infoChips: getLotInfoChips(responseLot, legs),
        hasParking: false,
        isLocked: false,
        sender: {
            name: originName,
            street: originStreet,
            address: `${originCountry}-${originZipCode} ${originCity}`
        },
        shipments: [],
        // ...(initialLeg.trafficFlow === '1' && {
        //     recipient: {
        //         name: destinationName,
        //         street: consigneeStreet,
        //         address: `${destinationCountry}, ${destinationCity}, ${destinationZipCode}`
        //     }
        // })

    };
};

export const getProductTypeValue = (productType: string): string => {
    const productTypeIconMap = {
        '01': FRESH,
        '02': ULTRA_FRESH,
        '03': DRY,
        '04': FROZEN,
        '05': LIQUID,
        '07': AMBIENT,
        '08': FRUITS_AND_VEGETABLES
    };

    return productTypeIconMap[productType as keyof typeof productTypeIconMap];
};

export const getProductTypeChip = (leg: LegResponseStructure): InfoLotChip => {
    const multipleNames = leg.productGroup.split(',').reduce<string[]>((acc, productType: string) => {
        const mappedValue = getProductTypeValue(productType);

        if (mappedValue) {
            acc.push(mappedValue);
        }

        return acc;
    }, []);

    return {
        multipleNames,
        name: ''
    };
};

export const getLotInfoChips = (lot: LotResponseStructure, legs: LegResponseStructure[]): InfoLotChip[] => {
    const lotTrafficFlows = new Set<string>();
    const lotProductTypes = new Set<string>();

    legs.forEach((leg: LegResponseStructure) => {
        lotTrafficFlows.add(leg.trafficFlow);
        leg.productGroup.split(',').forEach((productType: string) => {
            lotProductTypes.add(getProductTypeValue(productType));
        });
    });

    return [
        {
            label: Array.from(lotTrafficFlows).join(','),
            name: TRAFFIC_FLOW
        },
        {
            name: WEIGHT,
            label: roundToTwoDecimalPlaces(lot.total_weight)?.toString() || '0',
            fillPercentage: lot.total_weight / MAX_WEIGHT
        },
        {
            name: VOLUME_PALLET_SPACES,
            label: roundToTwoDecimalPlaces(lot.total_volume_pallet_spaces)?.toString() || '0',
            fillPercentage: lot.total_volume_pallet_spaces / MAX_VOLUME_PALLET_SPACES
        },
        {
            name: FLOOR_PALLET_SPACES,
            label: roundToTwoDecimalPlaces(lot.total_floor_pallet_spaces)?.toString() || '0',
            fillPercentage: lot.total_floor_pallet_spaces / MAX_FLOOR_PALLET_SPACES
        },
        ...(lot.pickupDateFrom ? [{
            name: DELIVERY_DATE_FROM,
            label: `${formatToLocalDate(new Date(lot.pickupDateFrom))}`
        }]
            : []),
        {
            name: PEOPLE,
            label: lot.total_clients_count?.toString() || '0'
        },
        {
            multipleNames: Array.from(lotProductTypes),
            name: ''
        }
    ];
};

export const getMappedLeg = (responseLeg: LegResponseStructure): DraggableCardConfig => {
    const {
        id,
        type,
        trafficFlow,
        shipmentNumber,
        consigneeServiceArea,
        consigneeName,
        consigneeStreet,
        consigneeCountry,
        consigneeZipCode,
        consigneeCity
    } = responseLeg;

    return {
        id,
        headerChips: getHeaderChips(type, trafficFlow),
        identificationNumber: shipmentNumber?.toString() || '0',
        shipmentId: responseLeg.shipmentId,
        consigneeServiceArea: consigneeServiceArea || 0,
        infoChips: getLegInfoChips(responseLeg),
        hasParking: false,
        isLocked: false,
        consigneeCountry,
        recipient: {
            name: consigneeName,
            street: consigneeStreet,
            address: `${consigneeCountry}-${consigneeZipCode} ${consigneeCity}`
        },
        // ...(responseLeg.trafficFlow === '1' && {
        //     sender: {
        //         name: responseLeg.originName,
        //         street: responseLeg.originStreet,
        //         address: `${responseLeg.originCity}, ${responseLeg.originZipCode}, ${responseLeg.originCountry}`
        //     }
        // })
    };
};

const getDateChipLabel = (dateFrom: Date, dateTo: Date): string => {
    const fromDate = formatToLocalDate(new Date(dateFrom));
    const toDate = formatToLocalDate(new Date(dateTo));
    const hasDash = fromDate && toDate && (fromDate !== toDate);

    return `${fromDate}${hasDash ? `-${toDate}` : ''}`;
};

const getTimeChipLabel = (timeFrom?: string, timeTo?: string): string => {
    if (timeFrom && timeTo && timeFrom !== '00:00' && timeTo !== '00:00') {
        return timeFrom === timeTo ? timeFrom : `${timeFrom}-${timeTo}`;
    }

    if (timeFrom && timeFrom !== '00:00') {
        return $localize`from ${timeFrom}`;
    }

    if (timeTo && timeTo !== '00:00') {
        return $localize`to ${timeTo}`;
    }

    return '';
};

const getTimeOrDefault = (date: Date): string => date ? getTimeOfDay(new Date(date)) : '00:00';
const getRoundedOrDefault = (value: number) => roundToTwoDecimalPlaces(value)?.toString() || '0';

export const getLegInfoChips = (leg: LegResponseStructure): InfoLotChip[] => {
    const chips: InfoLotChip[] = [];

    const deliveryTimeStart = getTimeOrDefault(leg.deliveryDateFrom);
    const deliveryTimeEnd = getTimeOrDefault(leg.deliveryDateTo);
    const pickupTimeStart = getTimeOrDefault(leg.pickupDateFrom);
    const pickupTimeEnd = getTimeOrDefault(leg.pickupDateTo);
    const legProductType = getProductTypeValue(leg.productGroup);

    chips.push({
        name: TRAFFIC_FLOW,
        label: leg.trafficFlow
    });

    if (leg.pickupDateFrom || leg.pickupDateTo) {
        chips.push({
            name: DELIVERY_DATE_FROM,
            label: getDateChipLabel(leg.pickupDateFrom, leg.pickupDateTo)
        });

        if (pickupTimeStart || pickupTimeEnd) {
            chips.push({
                name: PLANNED_TIME,
                label: getTimeChipLabel(pickupTimeStart, pickupTimeEnd)
            });
        }
    }

    if (leg.deliveryDateFrom || leg.deliveryDateTo) {
        chips.push({
            name: DELIVERY_DATE_TO,
            label: getDateChipLabel(leg.deliveryDateFrom, leg.deliveryDateTo)
        });

        if ((deliveryTimeStart || deliveryTimeEnd) && (deliveryTimeStart !== '00:00' || deliveryTimeEnd !== '00:00')) {
            chips.push({
                name: PLANNED_TIME,
                label: getTimeChipLabel(deliveryTimeStart, deliveryTimeEnd)
            });
        }
    }

    chips.push({
        name: WEIGHT,
        label: getRoundedOrDefault(leg.weight)
    });

    if (leg.volumePalletSpaces) {
        chips.push({
            name: VOLUME_PALLET_SPACES,
            label: getRoundedOrDefault(leg.volumePalletSpaces)
        });
    }

    if (leg.floorPalletSpaces) {
        chips.push({
            name: FLOOR_PALLET_SPACES,
            label: getRoundedOrDefault(leg.floorPalletSpaces)
        });
    }

    chips.push({ name: legProductType });

    return chips;
};

const TYPE_FILTER_VALUES = [LOT_FILTER_VALUES.VL, LOT_FILTER_VALUES.HL, LOT_FILTER_VALUES.NL];
const LEG_SORT_BRANCH = 'Branch';

export const VALUE_LOT__COLUMNS_KEYS_MAP: Record<string, string> = {
    [LOT_FILTER_VALUES.VL]: 'HasVLLeg',
    [LOT_FILTER_VALUES.HL]: 'HasHLLeg',
    [LOT_FILTER_VALUES.NL]: 'HasNLLeg'
};

const VALUE_LEG_COLUMNS_KEYS_MAP: Record<string, string> = {
    [LOT_FILTER_VALUES.VL]: 'IsVLType',
    [LOT_FILTER_VALUES.HL]: 'IsHLType',
    [LOT_FILTER_VALUES.NL]: 'IsNlType'
};

// const LOT_TO_LEG_KEYS_FILTER_MAP: Record<string, string> = {
//     [FILTERS_KEYS_MAP.TOTAL_WEIGHT]: 'Weight',
//     [FILTERS_KEYS_MAP.HAS_VOLUME_PALLET_RANGE]: 'VolumePalletSpaces',
//     [FILTERS_KEYS_MAP.HAS_FLOOR_PALLET_RANGE]: 'FloorPalletSpaces',
//     [FILTERS_KEYS_MAP.HAS_TRAFFIC_FLOW]: 'TrafficFlow',
//     [FILTERS_KEYS_MAP.HAS_PRODUCT_GROUP]: 'ProductGroup'
// };

export const getMappedLotsSortParams = (fields: ReorderableSortingType[]) => {
    const selectedColumns = fields.filter(field => field.value);
    const mappedColumns: MultipleSortParams[] = [];

    selectedColumns.map((field) => {
        if (TYPE_FILTER_VALUES.includes(field.key)) {
            mappedColumns.push(getCalcFieldSortObject(VALUE_LOT__COLUMNS_KEYS_MAP[field.key]));
        } else {
            mappedColumns.push({
                fieldName: field.key,
                sortDirection: field.sortDirection
            });
        }
    });

    if (!mappedColumns.length) {
        mappedColumns.push({
            fieldName: 'lotNumber',
            sortDirection: 0
        });
    }

    return mappedColumns;
};

export const getBranchSortParams = (field: ReorderableSortingType): MultipleSortParams[] => {
    return [
        {
            fieldName: 'consigneeCountry',
            sortDirection: 0 // Keep it always ASC
        },
        {
            fieldName: 'consigneeServiceArea',
            sortDirection: field.sortDirection
        }
    ];
};

export const getMappedLegsSortParams = (fields: ReorderableSortingType[]) => {
    const selectedColumns = fields.filter(field => field.value);
    const mappedColumns: MultipleSortParams[] = [];

    selectedColumns.map((field) => {
        switch (field.key) {
        case LOT_FILTER_VALUES.VL:
        case LOT_FILTER_VALUES.HL:
        case LOT_FILTER_VALUES.NL:
            mappedColumns.push(getCalcFieldSortObject(VALUE_LEG_COLUMNS_KEYS_MAP[field.key]));
            break;
        case LEG_SORT_BRANCH:
            mappedColumns.push(...getBranchSortParams(field));
            break;
        default:
            mappedColumns.push({
                fieldName: field.key,
                sortDirection: field.sortDirection
            });
        }
    });
    return mappedColumns;
};

//Those functions are commented for now, because currently they are not used for legs filtering

// const getMappedLegsFilterKeys = (filterFields: LotFilterType | TypeLotFilters) => {
//     return Object.entries(filterFields).reduce<LotFilterType | TypeLotFilters>((acc, [key, value]) => {
//         const currentKey = LOT_TO_LEG_KEYS_FILTER_MAP[key]
//             ? LOT_TO_LEG_KEYS_FILTER_MAP[key]
//             : key;

//         acc[currentKey] = value;

//         return acc;
//     }, {} as LotFilterType | TypeLotFilters);
// };

// export const getMappedLegsFiltersValue = (filterFields: LotFilterType | TypeLotFilters) => (
//     getMappedFiltersValue(getMappedLegsFilterKeys(filterFields), VALUE_LOT__COLUMNS_KEYS_MAP)
// );

const KEY_TO_VALUE_MAP_ORDER_DATA = {
    [TRANSPORT_ORDER_KEYS.TRANSPORT_MODE]: TRANSPORT_MODE_VALUE_TO_LABEL_MAP,
    [TRANSPORT_ORDER_KEYS.STATUS]: STATUS_VALUE_TO_LABEL_MAP
};

export const mapValueToLabelByKey = (orderData: TransportOrderListRecord, key: string ) => {
    const value = orderData[key as keyof TransportOrderListRecord];
    const valueMap = KEY_TO_VALUE_MAP_ORDER_DATA[key as keyof typeof KEY_TO_VALUE_MAP_ORDER_DATA];

    if (valueMap && typeof value === 'number') {
        orderData = {
            ...orderData,
            [key as keyof TransportOrderListRecord]: valueMap[value]
        };
    }

    return orderData;
};

export const getAppliedFiltersCount = <T extends Record<string, unknown>>(filterValue: T, defaultValues: T) : number => {
    return Object.entries(filterValue).reduce((acc, [key, value]) => {
        const currentValue = JSON.stringify(value);
        const defaultValue = JSON.stringify(defaultValues[key as keyof typeof defaultValues]);

        if (currentValue !== defaultValue) {
            acc += 1;
        }

        return acc;
    }, 0);
};

export const getHeaderChips = (type: string, trafficFlow: string): HeaderLotChip[] => {
    const headerChips: HeaderLotChip[] = [];

    const legTypes = TRAFFIC_MODE_LEG_TYPES[trafficFlow] || [];

    legTypes.forEach((legType) => {
        const label = LOT_FILTER_VALUES[legType as keyof typeof LOT_FILTER_VALUES];

        label && headerChips.push({
            label,
            active: legType === type
        });
    });

    return headerChips;
};

export const handleScrollToSelectedLot = (
    selectedLotCard: string | null,
    lazyLoadDirectives: QueryList<LazyLoadDirective> | undefined,
    resetViewInitialized: () => void
) => {
    if (selectedLotCard) {
        scrollToSelectedLot(selectedLotCard);
        lazyLoadDirectives?.forEach(dir => dir.disabled = false);
        resetViewInitialized();
    }
};

export function scrollToSelectedLot(lotId: string) {
    const lotElement = document.querySelector(`[data-lot-id="${lotId}"]`);
    if (lotElement) {
        lotElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
}

export const adjustScrollAfterPrepend = (
    container: HTMLElement | null,
    previousScrollHeight: number,
    resetShouldAdjustScroll: () => void
) => {
    if (container) {
        const newScrollHeight = container.scrollHeight;
        const scrollDiff = newScrollHeight - previousScrollHeight;
        container.scrollTop = scrollDiff;
        resetShouldAdjustScroll();
    }
};

export const getIsTransportOrderDisabled = (status: number): boolean => {
    return DISABLED_TRANSPORT_ORDER_STATUSES.includes(status);
};

export const getDraggedLegs = (dragData: PlanningDragData): DraggableCardConfig[] => {
    return Array.isArray(dragData) ? dragData : [dragData];
};

export const alertMultiDropNotImplemented = () => {
    alert('To be implemented');
};